import axios from 'axios';
import configData from "../../config.json";
import fileDownload from "js-file-download";
import { acquireAccessToken } from "../../auth/authTokenHelper";
import { msalInstance } from "../../auth/msalInstance"; // same one used by MsalProvider

export class BaseService {

    constructor(apiName, globalAuthState, uriType, timeout) {

        const authState = globalAuthState.get();
        this._globalAuthState = globalAuthState;
        if (authState?.isAuthenticated &&
            authState?.accessToken != null &&
            authState.idToken != null) {
            this._clientId = authState.idTokenClaims.clientId;
            this._isAuth = true;
        }
        else {
            this._isAuth = false;
        }

        if (uriType)
            this._uriType = uriType
        else
            this._uriType = "CORE";

        if (timeout)
            this._timeout = timeout;
        else
            this._timeout = 59000;

        this._urlBase = configData.API_URIS[this._uriType]

        this._url = this._urlBase + "/" + apiName;
        this._apiName = apiName;
        axios.defaults.headers.post['Content-Type'] = 'application/json; charset=utf-8';
    }

    get APIName() {
        return this._apiName;
    }

    _createAxiosInstance(ignoreSqlInclude, itemIncludes, isAnonymous = false, requestTimeout = this._timeout) {
        const instance = axios.create({
            timeout: requestTimeout,
        });

        instance.interceptors.request.use(async (config) => {

            if (!isAnonymous) {
                const authState = this._globalAuthState.get();
                const account = authState?.msalAccount;

                if (account) {
                    const token = await acquireAccessToken(msalInstance, account);
                    if (token) {
                        config.headers.Authorization = `Bearer ${token}`;
                    }
                }
            }

            if (this._clientId) config.headers["Client_Id"] = this._clientId;
                config.headers["C-DX-App-Id"] = configData.APP_ID;

            if (ignoreSqlInclude)
                config.headers["C-DX-Ignore-Sql-Include"] = ignoreSqlInclude;

            if (itemIncludes?.length)
                config.headers["C-DX-Item-Includes"] = itemIncludes;

            return config;
        });

        return instance;
    }

    _createAxiosError(e) {
        return e.response && e.response?.data !== "" ? e.response.data :
            ([{
                Exception: null,
                Message: {
                    DisplayMessage: e.message,
                    Code: "JSE1",
                    Info: e.stack,
                    IsFailMessage: true,
                    Name: "JS_ERROR"
                },
                InnerResults: [],
                Items: [],
                EventPersisted: false,
                EventDefinitionId: "SYSTEM",
                EventId: "00000000-0000-0000-0000-000000000000",
                CurrentPage: 0,
                TotalItemCount: 0,
                ItemsPerPage: 0,
                MessageDetails: e.stack,
                Success: false
            }]);
    }


    _createAxiosDownloadSuccess() {
        return [{
            Exception: null,
            Message: {
                DisplayMessage: "Download has been completed.",
                Code: "JSDS",
                Info: null,
                IsFailMessage: false,
                Name: "Download Success"
            },
            InnerResults: [],
            Items: [],
            EventPersisted: false,
            EventDefinitionId: "SYSTEM",
            EventId: "00000000-0000-0000-0000-000000000000",
            CurrentPage: 0,
            TotalItemCount: 0,
            ItemsPerPage: 0,
            MessageDetails: null,
            Success: true
        }];
    }


    operation(item, operationType, queryParams, isAnonymous = false) {
        const axiosInstance = this._createAxiosInstance(null, null, isAnonymous);
        axiosInstance.defaults.headers.common["c-dx-operation"] = operationType;

        let apiFunc = this._url;

        if (queryParams != null) {
            apiFunc = apiFunc + "?";
            for (let key in queryParams) {
                apiFunc = apiFunc + key + "=" + queryParams[key] + "&";
            }
            apiFunc = apiFunc.substring(0, apiFunc.length - 1);
        }

        return axiosInstance.post(apiFunc, item)
            .then(r => { return r.data; })
            .catch(e => { return this._createAxiosError(e); });
    }


    async postOperation(operationType, id, item, queryParams, isFile, isItem, requestTimeout = this._timeout) {
        const axiosInstance = this._createAxiosInstance(null, null, false, requestTimeout);
        axiosInstance.defaults.headers.common["c-dx-operation"] = operationType;

        if (isItem) {
            axiosInstance.defaults.headers.common["c-dx-operation-Format"] = 'Item';
        }

        let apiFunc = `${this._url}${(id !== undefined ? `/${id}` : '')}`;

        if (queryParams) {
            apiFunc = apiFunc + "?";
            for (let key in queryParams) {
                apiFunc = apiFunc + key + "=" + queryParams[key] + "&";
            }
            apiFunc = apiFunc.substring(0, apiFunc.length - 1);
        }

        let blobResponseType;
        if (isFile) {
            blobResponseType = { 'responseType': 'blob' };
        }
        return axiosInstance.post(apiFunc, item, blobResponseType)
            .then(r => {
                if ((r.headers["content-disposition"]?.indexOf("attachment") ?? -1) > -1) {
                    const fileName = r.headers["content-disposition"].split(";")[1].split("=")[1];
                    fileDownload(r.data, fileName, r.headers["content-type"]);
                    return this._createAxiosDownloadSuccess();
                }
                else {
                    return r.data;
                }
            })
            .catch(e => { return this._createAxiosError(e); });
    }

    async getFile(id, fileName) {
        const axiosInstance = this._createAxiosInstance();
        let apiFunc = `${this._urlBase}/File/Download/${id}`;
        let blobResponseType = { 'responseType': 'blob' };

        return axiosInstance.get(apiFunc, blobResponseType)
            .then(r => {
                if ((r.headers["content-disposition"]?.indexOf("attachment") ?? -1) > -1) {
                    if (!fileName)
                        fileName = r.headers["content-disposition"].split(";")[1].split("=")[1];

                    fileDownload(r.data, fileName, r.headers["content-type"]);
                    return this._createAxiosDownloadSuccess();
                }
                else {
                    fileDownload(r.data, fileName, r.headers["content-type"]);
                    return this._createAxiosDownloadSuccess();
                }
            })
            .catch(e => {
                return this._createAxiosError(e);
            });
    }

    async getOperation(operationType, id, queryParams, isFile, requestTimeout = this._timeout) {
        const axiosInstance = this._createAxiosInstance(null, null, false, requestTimeout);
        axiosInstance.defaults.headers.common["c-dx-operation"] = operationType;
        let apiFunc = `${this._url}${(id !== undefined ? `/${id}` : '')}`;

        if (queryParams) {
            apiFunc = apiFunc + "?";
            for (let key in queryParams) {
                apiFunc = apiFunc + key + "=" + queryParams[key] + "&";
            }
            apiFunc = apiFunc.substring(0, apiFunc.length - 1);
        }
        let blobResponseType;

        if (isFile) {
            blobResponseType = { 'responseType': 'blob' };
        }

        return axiosInstance.get(apiFunc, blobResponseType)
            .then(r => {
                if ((r.headers["content-disposition"]?.indexOf("attachment") ?? -1) > -1) {
                    const fileName = r.headers["content-disposition"].split(";")[1].split("=")[1];
                    fileDownload(r.data, fileName, r.headers["content-type"]);
                    return this._createAxiosDownloadSuccess();
                }
                else {
                    return r.data;
                }
            })
            .catch(e => {
                return this._createAxiosError(e);
            });
    }

}